import { NoStockLocationNotice } from '../../app/no-stock-location-notice.js';
import { PackageOpen as CardIconPackageOpen } from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { Button, Combobox, IconButton, Input, Select, useSnackbar } from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { getApiError, type MaterialUsageLine } from '../../lib/api.js';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { unitLabels } from './units.js';
import { RegisteredLines, ReverseDialog } from './usage-lines.js';
import type { ClosingMaterials, PlannedClosingMaterial } from './use-closing-materials.js';

const usageKey = ['company', 'material-usage'] as const;

/// "Rascunho salvo às 10:18 por Ana Souza", ou o aviso de que está salvando.
export function DraftStatus({ materials }: { materials: ClosingMaterials }) {
  if (materials.saving) {
    return (
      <span role="status" className="inline-flex items-center gap-1.5 text-xs text-content-muted">
        <span aria-hidden="true" className="size-1.5 rounded-full bg-warning" />
        Salvando rascunho…
      </span>
    );
  }
  if (!materials.draftInfo) return <span role="status" className="sr-only" />;
  return (
    <span role="status" className="inline-flex items-center gap-1.5 text-xs text-content-muted">
      <span aria-hidden="true" className="size-1.5 rounded-full bg-positive" />
      Rascunho salvo às {materials.draftInfo.at} por {materials.draftInfo.by}
    </span>
  );
}

/// A seção de material do fechamento: o que já foi registrado, com estorno, e o rascunho do que
/// ainda vai ser registrado.
export function ClosingMaterialsSection({
  materials,
  planned,
  canRegister,
  canReverse,
  completed,
  timeZone,
}: {
  materials: ClosingMaterials;
  planned: readonly PlannedClosingMaterial[] | null;
  canRegister: boolean;
  canReverse: boolean;
  /// Concluído, o registro deixa de ser feito pelo botão de concluir e ganha o botão próprio.
  completed: boolean;
  timeZone: string;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [reverseTarget, setReverseTarget] = useState<MaterialUsageLine>();
  const [registering, setRegistering] = useState(false);
  const showPlanned = planned !== null;
  const hasPlanned = (planned ?? []).length > 0;

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <ContentCardHeader
          title="Material usado"
          id="closing-materials-title"
          icon={<CardIconPackageOpen className="size-4" />}
        />
        <p className="mt-1 max-w-prose text-xs text-content-muted">
          {completed
            ? 'Lançar um produto esquecido ou estornar uma linha errada continua possível depois de concluir.'
            : hasPlanned
              ? 'Veio preenchido com o previsto. Corrija o que foi diferente.'
              : 'Informe o que saiu do estoque. Se nada foi usado, deixe vazio.'}
        </p>
      </div>
      {canRegister && materials.draftInfo && !materials.saving ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            void materials
              .discard()
              .then(() => snackbar.success('Rascunho descartado.'))
              .catch((cause: unknown) => snackbar.failure(getApiError(cause)))
          }
        >
          Descartar rascunho
        </Button>
      ) : null}
    </div>
  );

  if (materials.loading) {
    return (
      <section aria-labelledby="closing-materials-title" className="grid gap-4">
        {header}
        <LoadingCard label="Carregando material" />
      </section>
    );
  }
  if (materials.error || !materials.detail) {
    return (
      <section aria-labelledby="closing-materials-title" className="grid gap-4">
        {header}
        <ErrorCard message={getApiError(materials.error)} />
      </section>
    );
  }

  const plannedOf = (productId: string) =>
    (planned ?? []).find((item) => item.productId === productId)?.quantity ?? '0';
  const productOptions = materials.catalog.map((product) => ({
    value: product.id,
    label: product.displayName,
  }));

  return (
    <section aria-labelledby="closing-materials-title" className="grid gap-4">
      {header}
      <RegisteredLines
        lines={materials.detail.lines}
        canReverse={canReverse}
        timeZone={timeZone}
        onReverse={setReverseTarget}
      />
      {canRegister ? (
        <div className="grid gap-3">
          {materials.detail.lines.length > 0 || completed ? (
            <h3 className="text-sm font-semibold">{completed ? 'Lançar mais' : 'A registrar'}</h3>
          ) : null}
          {materials.lines.length === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-surface-border p-4 text-sm text-content-muted">
              <span>
                {completed || materials.detail.lines.length > 0
                  ? 'Nada a lançar.'
                  : 'Nenhum material usado neste atendimento.'}
              </span>
              <Button variant="outline" onClick={materials.add}>
                <Plus className="size-4" />
                Adicionar produto
              </Button>
            </div>
          ) : (
            <>
              <ul className="grid gap-3">
                {materials.lines.map((line) => {
                  const product = materials.catalog.find((item) => item.id === line.productId);
                  const unit = product ? unitLabels[product.baseUnit] : '';
                  const lineErrors = materials.errors[line.key] ?? {};
                  return (
                    <li
                      key={line.key}
                      className="flex items-start gap-2 rounded-2xl border border-surface-border p-3"
                    >
                      {/* Produto e usado numa linha, local e lote na outra: quatro campos lado a
                          lado cortavam o nome do local e do lote na coluna do fechamento. */}
                      <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 sm:items-start">
                        <div>
                          <Combobox
                            label="Produto"
                            placeholder="Buscar produto"
                            selectedLabel={
                              productOptions.find((option) => option.value === line.productId)
                                ?.label ?? ''
                            }
                            options={productOptions}
                            emptyLabel="Nenhum produto com esse nome"
                            onClear={() => materials.update(line.key, { productId: '', lotId: '' })}
                            onSelect={(choice) => {
                              if (choice.kind === 'option')
                                materials.update(line.key, { productId: choice.value, lotId: '' });
                            }}
                          />
                          {lineErrors.productId ? (
                            <p role="alert" className="mt-1.5 text-xs text-danger">
                              {lineErrors.productId}
                            </p>
                          ) : null}
                        </div>
                        <div>
                          <Input
                            label={`Usado${unit ? ` (${unit})` : ''}`}
                            value={line.quantity}
                            inputMode="decimal"
                            onChange={(event) =>
                              materials.update(line.key, { quantity: event.target.value })
                            }
                            error={lineErrors.quantity}
                          />
                          {showPlanned && line.productId ? (
                            <Difference
                              planned={plannedOf(line.productId)}
                              used={line.quantity}
                              unit={unit}
                            />
                          ) : null}
                        </div>
                        {materials.locationOptions.length === 0 ? (
                          <NoStockLocationNotice />
                        ) : (
                          <Select
                            label="Local"
                            value={line.locationId}
                            options={materials.locationOptions}
                            onValueChange={(locationId) =>
                              materials.update(line.key, { locationId, lotId: '' })
                            }
                          />
                        )}
                        {product?.requiresLot ? (
                          <Select
                            label="Lote"
                            value={materials.lotOf(line)}
                            options={materials.lotOptionsOf(line)}
                            onValueChange={(lotId) => materials.update(line.key, { lotId })}
                          />
                        ) : null}
                      </div>
                      <div className="shrink-0 pt-6">
                        <IconButton
                          label="Tirar linha"
                          tone="danger"
                          icon={<Trash2 className="size-4" />}
                          onClick={() => materials.remove(line.key)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={materials.add}>
                  <Plus className="size-4" />
                  Adicionar produto
                </Button>
                {completed ? (
                  <Button
                    loading={registering}
                    onClick={() => {
                      setRegistering(true);
                      void materials
                        .register()
                        .then((ok) => {
                          if (ok) snackbar.success('Material registrado e baixado do estoque.');
                        })
                        .finally(() => setRegistering(false));
                    }}
                  >
                    Registrar material
                  </Button>
                ) : null}
              </div>
            </>
          )}
          {materials.requestError ? <RequestAlert message={materials.requestError} /> : null}
        </div>
      ) : materials.detail.lines.length === 0 ? (
        <p className="text-sm text-content-muted">
          Nenhum material registrado. Registrar consumo pede permissão própria.
        </p>
      ) : null}
      {reverseTarget ? (
        <ReverseDialog
          line={reverseTarget}
          onOpenChange={() => setReverseTarget(undefined)}
          onReversed={async () => {
            await queryClient.invalidateQueries({ queryKey: usageKey });
            setReverseTarget(undefined);
            snackbar.success('Consumo estornado e devolvido ao estoque.');
          }}
        />
      ) : null}
    </section>
  );
}

/// Usado contra previsto, na linha. Diferença em texto, não só em cor.
function Difference({ planned, used, unit }: { planned: string; used: string; unit: string }) {
  const value = Number(used.trim().replace(',', '.'));
  const expected = Number(planned);
  if (!used.trim() || !Number.isFinite(value)) {
    return (
      <p className="mt-1.5 text-xs text-content-muted">
        Previsto: {planned} {unit}
      </p>
    );
  }
  if (expected === 0) return <p className="mt-1.5 text-xs text-warning">Fora do previsto</p>;
  const difference = Math.round((value - expected) * 1_000_000) / 1_000_000;
  if (difference === 0) return <p className="mt-1.5 text-xs text-positive">Igual ao previsto</p>;
  const amount = Math.abs(difference).toLocaleString('pt-BR', { maximumFractionDigits: 6 });
  return (
    <p className={`mt-1.5 text-xs ${difference > 0 ? 'text-warning' : 'text-content-secondary'}`}>
      {difference > 0 ? `+${amount}` : `−${amount}`} {unit} do previsto ({planned} {unit})
    </p>
  );
}
